import { engineComponent } from "neo-common";
import { ComponentTypes } from "../Components/ComponentTypes";
import { Reel } from "../GameComponents/Reel";
import { Symbol } from "../GameComponents/Symbol";
import { Tweens } from "../Components/Effects/Tweens";
import { Settings } from "./Settings";
import { SymbolExtended } from "./SymbolExtended";

/**
 * ReelExtended handles:
 * - z-order
 * - usage of Normal instead of Rotation
 */
@engineComponent(ComponentTypes.DisplayObject)
export class ReelExtended extends Reel {
    public static Tease: boolean = false;

    public spinStop(force: boolean = false, customDelay: number = 0, useCustomDelay: boolean = false): void {
        this.stopTimer.stop();

        if (force) {
            this.rollSpeed = this.forceSpeed;

            if (useCustomDelay) {
                this.stopTimer.start(customDelay);
            } else {
                this.shouldBeStopping = true;
            }
        } else {
            this.stopTimer.start(customDelay !== 0 ? customDelay : this.stopDelay);
        }
    }

    //******************************************************************************************************************
    //  SPIN PRE ROLL
    //******************************************************************************************************************

    protected spinPreRoll(): void {
        if (this.currentSymbols != null) {
            for (let i: number = 0; i < this.currentSymbols.length; i++) {
                if (
                    this.currentSymbols[i].hasCase("START_ROTATION") &&
                    this.currentSymbols[i].getCurrentCase() !== Symbol.CaseEmpty
                ) {
                    this.currentSymbols[i].goToCase("START_ROTATION");
                }

                if (Settings.SymbolsNoTrim.indexOf(this.currentSymbols[i].id + 1) != -1) {
                    this.currentSymbols[i].visible = false;
                }
            }
        }

        Tweens.removeTweens(this, "position");
        Tweens.positionTween(this, {
            tweenX: [this.positionX, this.positionX, this.positionX],
            tweenY: [this.positionY, this.positionY - this.preRoll, this.positionY],
            tweenDuration: this.preRollSpeed * 2,
            tweenDelay: this.startDelay,
            callbackFunction: this.spinRoll,
            callbackScope: this,
        });
    }

    //******************************************************************************************************************
    //  SPIN ROLL
    //******************************************************************************************************************

    protected spinRoll(): void {
        if (this.currentSymbols != null) {
            for (let i: number = 0; i < this.currentSymbols.length; i++) {
                if (this.currentSymbols[i] && Settings.SymbolsNoTrim.indexOf(this.currentSymbols[i].id + 1) != -1) {
                    this.currentSymbols[i].visible = true;
                }
            }
        }

        Tweens.removeTweens(this, "position");
        Tweens.positionTween(this, {
            tweenX: [this.positionX, this.positionX],
            tweenY: [this.positionY, this.positionY + this.symbolHeight],
            tweenDuration: this.rollSpeed,
            callbackFunction: this.updateReel,
            callbackScope: this,
        });
    }

    //******************************************************************************************************************
    //  SHOW SYMBOLS, SPIN ROLL
    //******************************************************************************************************************

    /**
     * Sorts symbols according to their zIndex attribute, if applicable
     */
    sortSymbols() {
        if (!this.symbols.hasZindex) {
            return;
        }
        let addQueue: Symbol[] = [];
        this.currentSymbols.forEach((symbol) => {
            addQueue.push(<Symbol>symbol);
        });
        addQueue.sort((a, b) => a.zIndex - b.zIndex);
        addQueue.forEach((symbol) => {
            this.addChild(symbol);
        });
    }

    showSymbols(useTransform: boolean = false) {
        super.showSymbols(useTransform);
        this.sortSymbols();
    }

    protected updateReel(): void {
        this.returnSymbol(this.currentSymbols.pop());

        if (this.shouldBeStopping && !this.isStopping) {
            if (this.previousReel === null) {
                this.isStopping = true;
            } else if (this.previousReel.isStopping) {
                this.isStopping = true;
            }
        }

        this.stripIndex = (this.stripIndex - 1 + this.strip.length) % this.strip.length;

        let symbolId = this.strip[this.stripIndex];
        let symbolCase = Symbol.CaseBlur;

        if (this.isStopping) {
            if (this.stoppingCounter < this.symbolBottomCount) {
                symbolId =
                    this.strip[
                        (this.data.index +
                            this.symbolBottomCount +
                            this.symbolCount -
                            this.stoppingCounter -
                            1 +
                            this.strip.length) %
                            this.strip.length
                    ];
                symbolCase =
                    this.stoppingCounter === this.symbolBottomCount - 1 ? Symbol.CaseRotation : Symbol.CaseBlur;
            } else if (this.stoppingCounter < this.symbolBottomCount + this.symbolCount) {
                symbolId = this.data.symbols[this.symbolBottomCount + this.symbolCount - this.stoppingCounter - 1];
                symbolCase = Symbol.CaseRotation;

                if (
                    this.data.overlay &&
                    this.data.overlay[this.symbolBottomCount + this.symbolCount - this.stoppingCounter - 1] != -1
                ) {
                    symbolCase = Symbol.CaseEmpty;

                    if (
                        this.data.transform &&
                        this.data.transform[this.symbolBottomCount + this.symbolCount - this.stoppingCounter - 1] != -1
                    ) {
                        symbolCase = Symbol.CaseRotation;
                    }
                }
            } else {
                symbolId =
                    this.strip[
                        (this.data.index +
                            this.symbolBottomCount +
                            this.symbolCount -
                            this.stoppingCounter -
                            1 +
                            this.strip.length) %
                            this.strip.length
                    ];
                symbolCase =
                    this.stoppingCounter === this.symbolBottomCount + this.symbolCount
                        ? Symbol.CaseRotation
                        : Symbol.CaseBlur;
            }

            this.stoppingCounter++;
        }

        const symbol = this.getSymbol(symbolId - 1, symbolCase);
        this.currentSymbols.unshift(symbol);

        if (this.symbols.hasZindex) {
            let added = false;
            for (let index = 0; index < this.children.length; index++) {
                let child = <Symbol>this.children[index];
                if (child.zIndex >= symbol.zIndex) {
                    this.addChildAt(symbol, index);
                    added = true;
                    break;
                }
            }
            if (!added) {
                this.addChild(symbol);
            }
        } else {
            this.addChild(this.currentSymbols[0]);
        }

        for (let i = 0; i < this.currentSymbols.length; i++) {
            const symbol = this.currentSymbols[i];
            symbol.x = -symbol.offsetX;
            symbol.y = this.symbolHeight * (i - this.symbolTopCount) - symbol.offsetY;
        }

        if (
            this.isStopping &&
            this.stoppingCounter === this.symbolBottomCount + this.symbolCount + this.symbolTopCount
        ) {
            this.spinPostRoll();
        } else {
            this.spinRoll();
        }
    }

    //******************************************************************************************************************
    //  SYMBOLS
    //******************************************************************************************************************

    protected getSymbol(symbolId: number, symbolCase: string): Symbol {
        const symbol = super.getSymbol(symbolId, symbolCase);

        if (ReelExtended.Tease) {
            if (symbol.getCurrentCase() === Symbol.CaseBlur && symbol.id !== Settings.SymbolTeasing - 1) {
                // if (symbol.getCurrentCase() === Symbol.CaseBlur && (symbol.id !== Settings.SymbolTeasing-1 && symbol.id != 12 - 1)) {
                // if (symbol.getCurrentCase() === Symbol.CaseBlur && Settings.SymbolTeasing.map(s => s -1).indexOf(symbol.id) === -1) {
                symbol.goToCase(SymbolExtended.CaseTeasingBlur);
                //symbol.alpha = 0.3;
            } else if (symbol.getCurrentCase() === Symbol.CaseRotation && symbol.id !== Settings.SymbolTeasing - 1) {
                // } else if(symbol.getCurrentCase() === Symbol.CaseRotation &&  Settings.SymbolTeasing.map(s => s -1).indexOf(symbol.id) === -1) {
                symbol.goToCase(SymbolExtended.CaseTeasingRotation);
            }
        } else {
            symbol.alpha = 1;
        }

        return symbol;
    }

    public hasSymbol(symbolId: number): boolean {
        return this.data.symbols.indexOf(symbolId) !== -1;
    }

    public hasDice(): boolean {
        return this.hasSymbol(10);
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/ReelExtended.ts
 **/